export type SeedRow = Record<string, unknown> & { id: string };

const isoDate = (dayOffset: number): string => {
  const date = new Date(Date.UTC(2025, 0, 1 + dayOffset, 14, 0, 0));
  return date.toISOString();
};

const dateOnly = (dayOffset: number): string => isoDate(dayOffset).slice(0, 10);

const organizations = Array.from({ length: 10 }, (_, index) => ({
  id: `org-${index + 1}`,
  name: `${['Northstar', 'Pioneer', 'Summit', 'Lighthouse', 'Orbit', 'Catalyst', 'Harbor', 'Beacon', 'Ridge', 'Acorn'][index]} Events`,
  slug: `org-${index + 1}`,
  description: `Organization ${index + 1} that runs regional conferences and workshops.`,
  logo_url: `https://images.example.com/logos/org-${index + 1}.png`,
  website_url: `https://org-${index + 1}.example.com`,
  metadata: JSON.stringify({ tier: index % 3 === 0 ? 'enterprise' : 'standard' }),
  createdAt: isoDate(index),
  updatedAt: isoDate(index + 2),
}));

const venues = Array.from({ length: 10 }, (_, index) => ({
  id: `venue-${index + 1}`,
  org_id: organizations[index % organizations.length].id,
  name: `${['Grand Hall', 'Expo Center', 'Riverside Studio', 'Skyline Loft', 'Harbor Pavilion', 'Metro Hub', 'Innovation Campus', 'Summit Point', 'City Atrium', 'Garden Forum'][index]}`,
  description: `Primary event venue ${index + 1}.`,
  address: `${100 + index} Market Street`,
  city: ['San Francisco', 'Austin', 'Seattle', 'Boston', 'Chicago'][index % 5],
  state: ['CA', 'TX', 'WA', 'MA', 'IL'][index % 5],
  country: 'US',
  postal_code: `94${100 + index}`,
  latitude: 37.7 + index * 0.01,
  longitude: -122.4 + index * 0.01,
  capacity: 250 + index * 40,
  amenities: 'wifi,parking,projector,catering',
  image_url: `https://images.example.com/venues/venue-${index + 1}.jpg`,
  website_url: `https://venues.example.com/${index + 1}`,
  metadata: JSON.stringify({ indoor: index % 2 === 0 }),
  createdAt: isoDate(index + 1),
  updatedAt: isoDate(index + 4),
}));

const clients = Array.from({ length: 12 }, (_, index) => ({
  id: `client-${index + 1}`,
  name: ['Alice Johnson', 'Brian Lee', 'Carla Gomez', 'Darius Khan', 'Elena Rossi', 'Farah Ahmed', 'Gavin Turner', 'Hiro Tanaka', 'Iris Chen', 'Jonas Meyer', 'Kiara Patel', 'Liam Brooks'][index],
  email: `client${index + 1}@example.com`,
  phone: `+1-555-01${String(index).padStart(2, '0')}`,
  company: `${['Acme', 'Globex', 'Initech', 'Umbrella', 'Wayne', 'Stark', 'Wonka', 'Pied Piper', 'Hooli', 'Initrode', 'Massive Dynamic', 'Soylent'][index]} Inc`,
  status: ['active', 'inactive', 'pending'][index % 3],
  industry: ['SaaS', 'Healthcare', 'Finance', 'Retail'][index % 4],
  website: `https://client${index + 1}.example.com`,
  address: `${200 + index} Client Ave, Suite ${index + 10}`,
  notes: `High-priority account manager note #${index + 1}.`,
  createdAt: isoDate(index + 3),
  updatedAt: isoDate(index + 6),
}));

const events = Array.from({ length: 12 }, (_, index) => ({
  id: `event-${index + 1}`,
  org_id: organizations[index % organizations.length].id,
  title: `${['Growth Summit', 'Product Expo', 'Dev Leaders', 'Design Forward', 'ScaleOps Live', 'Commerce Now', 'AI Builders', 'Cloud Native Day', 'DataWeek', 'Security Forum', 'PeopleOps Connect', 'Revenue Camp'][index]} 2025`,
  slug: `event-${index + 1}`,
  description: `Full day event for track ${index + 1}.`,
  short_description: 'A focused, practical event for modern teams.',
  cover_image_url: `https://images.example.com/events/event-${index + 1}.jpg`,
  category: ['conference', 'meetup', 'workshop'][index % 3],
  format: ['in_person', 'virtual', 'hybrid'][index % 3],
  status: ['draft', 'published', 'completed'][index % 3],
  start_at: isoDate(index + 10),
  end_at: isoDate(index + 11),
  timezone: 'America/Los_Angeles',
  venue_id: venues[index % venues.length].id,
  virtual_url: `https://stream.example.com/events/${index + 1}`,
  max_attendees: 150 + index * 25,
  current_attendee_count: 50 + index * 10,
  is_sold_out: index % 5 === 0,
  waitlist_enabled: index % 2 === 0,
  refund_policy: ['none', 'flexible', 'strict'][index % 3],
  currency: 'USD',
  tags: 'tech,b2b,networking',
  cancellation_reason: index % 7 === 0 ? 'Venue maintenance' : '',
  published_at: isoDate(index + 8),
  canceled_at: index % 7 === 0 ? isoDate(index + 9) : '',
  metadata: JSON.stringify({ trackCount: 3 + (index % 4) }),
  createdAt: isoDate(index + 7),
  updatedAt: isoDate(index + 9),
}));

const users = Array.from({ length: 10 }, (_, index) => ({
  id: `user-${index + 1}`,
  name: ['Mia Davis', 'Noah Clark', 'Olivia Perez', 'Paul Green', 'Quinn Young', 'Rita Bell', 'Sam Evans', 'Tina Morris', 'Uma Fisher', 'Victor Hall'][index],
  email: `user${index + 1}@example.com`,
  role: ['organizer', 'attendee', 'speaker'][index % 3],
  createdAt: isoDate(index + 5),
  updatedAt: isoDate(index + 6),
}));

const promoCodes = Array.from({ length: 10 }, (_, index) => ({
  id: `promo-${index + 1}`,
  code: `SAVE${(index + 1) * 5}`,
  discount_type: index % 2 === 0 ? 'percent' : 'fixed',
  discount_value: index % 2 === 0 ? 10 + index : 500 + index * 20,
  max_uses: 100 + index * 10,
  max_uses_per_user: 2,
  applicable_ticket_type_ids: `ticket-${(index % 10) + 1}`,
  valid_from: isoDate(index + 1),
  valid_until: isoDate(index + 40),
  createdAt: isoDate(index + 2),
  updatedAt: isoDate(index + 3),
}));

const tickets = Array.from({ length: 10 }, (_, index) => ({
  id: `ticket-${index + 1}`,
  event_id: events[index % events.length].id,
  name: `${['General Admission', 'VIP', 'Workshop', 'Student', 'Partner'][index % 5]} Pass`,
  price_cents: 9900 + index * 500,
  createdAt: isoDate(index + 10),
  updatedAt: isoDate(index + 11),
}));

export const seedDataByEntity: Record<string, SeedRow[]> = {
  clients,
  leads: Array.from({ length: 12 }, (_, index) => ({
    id: `lead-${index + 1}`,
    name: ['Adam Reid', 'Bella King', 'Chris Stone', 'Diana Ford', 'Ethan Price', 'Fiona Webb', 'George Nash', 'Hanna Lowe', 'Ian Moss', 'Julia Ray', 'Kyle Pope', 'Lana Wise'][index],
    email: `lead${index + 1}@example.com`,
    phone: `+1-444-02${String(index).padStart(2, '0')}`,
    company: `${['Nimbus', 'Vertex', 'Pulse', 'Zenith'][index % 4]} Labs`,
    source: ['website', 'referral', 'cold_call', 'event', 'other'][index % 5],
    status: ['new', 'contacted', 'qualified', 'disqualified'][index % 4],
    notes: `Lead entered from campaign ${index + 1}.`,
    createdAt: isoDate(index + 2),
    updatedAt: isoDate(index + 4),
  })),
  opportunities: Array.from({ length: 12 }, (_, index) => ({
    id: `opportunity-${index + 1}`,
    title: `${['Platform Expansion', 'Renewal', 'Migration', 'Consulting Retainer'][index % 4]} #${index + 1}`,
    clientId: clients[index % clients.length].id,
    status: ['open', 'won', 'lost', 'pending'][index % 4],
    value: 20000 + index * 4500,
    probability: 35 + (index % 6) * 10,
    expectedCloseDate: dateOnly(index + 25),
    createdAt: isoDate(index + 3),
    updatedAt: isoDate(index + 8),
  })),
  proposals: Array.from({ length: 12 }, (_, index) => ({
    id: `proposal-${index + 1}`,
    title: `Proposal ${index + 1}`,
    clientId: clients[index % clients.length].id,
    status: ['draft', 'sent', 'accepted', 'rejected'][index % 4],
    value: 12000 + index * 1200,
    expiresAt: dateOnly(index + 20),
    createdAt: isoDate(index + 1),
    updatedAt: isoDate(index + 5),
  })),
  contracts: Array.from({ length: 12 }, (_, index) => ({
    id: `contract-${index + 1}`,
    title: `Service Agreement ${index + 1}`,
    clientId: clients[index % clients.length].id,
    status: ['active', 'draft', 'expired', 'terminated'][index % 4],
    value: 30000 + index * 3000,
    startDate: dateOnly(index + 5),
    endDate: dateOnly(index + 370),
    createdAt: isoDate(index + 4),
    updatedAt: isoDate(index + 9),
  })),
  questionnaires: Array.from({ length: 12 }, (_, index) => ({
    id: `questionnaire-${index + 1}`,
    title: `Discovery Form ${index + 1}`,
    clientId: clients[index % clients.length].id,
    status: ['draft', 'sent', 'completed', 'overdue'][index % 4],
    dueDate: dateOnly(index + 15),
    createdAt: isoDate(index + 2),
    updatedAt: isoDate(index + 6),
  })),
  'team-members': Array.from({ length: 10 }, (_, index) => ({
    id: `member-${index + 1}`,
    name: ['Avery Scott', 'Blake Jordan', 'Casey Turner', 'Devon White', 'Emery Brown', 'Finley Moore', 'Gray Kelly', 'Harper Adams', 'Indigo Flores', 'Jules Cooper'][index],
    email: `team${index + 1}@clientforce.example.com`,
    role: ['admin', 'manager', 'sales', 'support'][index % 4],
    department: ['Sales', 'Customer Success', 'Operations'][index % 3],
    isActive: index % 4 !== 0,
    createdAt: isoDate(index + 3),
    updatedAt: isoDate(index + 7),
  })),
  organizations,
  events,
  sessions: Array.from({ length: 12 }, (_, index) => ({
    id: `session-${index + 1}`,
    title: `Session ${index + 1}: ${['Growth', 'Design', 'Security', 'AI'][index % 4]} Patterns`,
    description: 'A practical talk with real implementation details.',
    speaker_ids: `speaker-${(index % 10) + 1}`,
    room: ['Main Hall', 'Studio A', 'Room 204'][index % 3],
    start_at: isoDate(index + 12),
    end_at: isoDate(index + 12) ,
    capacity: 80 + index * 10,
    track: ['Product', 'Engineering', 'Marketing'][index % 3],
    order: index + 1,
    metadata: JSON.stringify({ level: ['beginner', 'intermediate', 'advanced'][index % 3] }),
    createdAt: isoDate(index + 11),
    updatedAt: isoDate(index + 13),
  })),
  'ticket-types': Array.from({ length: 12 }, (_, index) => ({
    id: `ticket-type-${index + 1}`,
    name: `${['General', 'VIP', 'Workshop', 'Community'][index % 4]} Ticket`,
    description: 'Ticket option with access rules and capacity limits.',
    price_cents: 9900 + index * 700,
    quantity_total: 150 + index * 20,
    sale_starts_at: isoDate(index + 3),
    sale_ends_at: isoDate(index + 30),
    min_per_order: 1,
    max_per_order: 8,
    session_ids: `session-${(index % 12) + 1}`,
    metadata: JSON.stringify({ bundle: index % 2 === 0 }),
    createdAt: isoDate(index + 2),
    updatedAt: isoDate(index + 5),
  })),
  orders: Array.from({ length: 12 }, (_, index) => ({
    id: `order-${index + 1}`,
    event_id: events[index % events.length].id,
    user_id: users[index % users.length].id,
    order_number: `EH-${202500 + index}`,
    status: ['pending', 'paid', 'refunded', 'canceled'][index % 4],
    items: JSON.stringify([{ ticketTypeId: `ticket-type-${(index % 12) + 1}`, qty: 2 }]),
    subtotal_cents: 25000 + index * 1000,
    discount_cents: index % 2 === 0 ? 1500 : 0,
    total_cents: 23500 + index * 1000,
    currency: 'USD',
    promo_code_id: promoCodes[index % promoCodes.length].id,
    promo_code: promoCodes[index % promoCodes.length].code,
    payment_provider: ['stripe', 'adyen'][index % 2],
    payment_intent_id: `pi_${index + 1}`,
    checkout_url: `https://checkout.example.com/${index + 1}`,
    customer_email: users[index % users.length].email,
    customer_name: users[index % users.length].name,
    refund_amount_cents: index % 4 === 2 ? 10000 : 0,
    refunded_at: index % 4 === 2 ? isoDate(index + 15) : '',
    canceled_at: index % 4 === 3 ? isoDate(index + 14) : '',
    confirmed_at: index % 4 === 1 ? isoDate(index + 13) : '',
    metadata: JSON.stringify({ channel: 'web' }),
    createdAt: isoDate(index + 6),
    updatedAt: isoDate(index + 8),
  })),
  attendees: Array.from({ length: 12 }, (_, index) => ({
    id: `attendee-${index + 1}`,
    event_id: events[index % events.length].id,
    user_id: users[index % users.length].id,
    ticket_id: tickets[index % tickets.length].id,
    ticket_type_name: ['General', 'VIP', 'Workshop'][index % 3],
    full_name: users[index % users.length].name,
    email: users[index % users.length].email,
    status: ['registered', 'checked_in', 'canceled'][index % 3],
    checked_in_at: index % 3 === 1 ? isoDate(index + 16) : '',
    custom_fields: JSON.stringify({ tshirt: ['S', 'M', 'L'][index % 3] }),
    notes: `Attendee preference note ${index + 1}.`,
    createdAt: isoDate(index + 5),
    updatedAt: isoDate(index + 9),
  })),
  waitlist: Array.from({ length: 10 }, (_, index) => ({
    id: `waitlist-${index + 1}`,
    event_id: events[index % events.length].id,
    email: `wait${index + 1}@example.com`,
    full_name: ['Will Carter', 'Xena Boyd', 'Yara Kim', 'Zaid Long', 'Ari Cole', 'Bryn Lake', 'Cleo Hart', 'Drew Snow', 'Eden Knox', 'Flynn Reed'][index],
    status: ['pending', 'invited', 'converted'][index % 3],
    createdAt: isoDate(index + 7),
    updatedAt: isoDate(index + 10),
  })),
  'promo-codes': promoCodes,
  speakers: Array.from({ length: 10 }, (_, index) => ({
    id: `speaker-${index + 1}`,
    name: ['Grace Kim', 'Henry Moss', 'Isabel Diaz', 'Jack Nguyen', 'Kendra Liu', 'Leo Park', 'Mona Singh', 'Nate Cole', 'Opal West', 'Parker Dunn'][index],
    title: ['VP Product', 'CTO', 'Principal Engineer', 'Design Lead'][index % 4],
    bio: `Speaker ${index + 1} with deep domain expertise.`,
    photo_url: `https://images.example.com/speakers/speaker-${index + 1}.jpg`,
    email: `speaker${index + 1}@example.com`,
    linkedin_url: `https://linkedin.com/in/speaker-${index + 1}`,
    twitter_handle: `@speaker${index + 1}`,
    website_url: `https://speaker${index + 1}.example.com`,
    session_ids: `session-${(index % 12) + 1}`,
    order: index + 1,
    metadata: JSON.stringify({ featured: index % 3 === 0 }),
    createdAt: isoDate(index + 2),
    updatedAt: isoDate(index + 6),
  })),
  venues,
  webhooks: Array.from({ length: 10 }, (_, index) => ({
    id: `webhook-${index + 1}`,
    url: `https://hooks.example.com/runtime/${index + 1}`,
    events: 'order.created,order.updated,event.published',
    createdAt: isoDate(index + 1),
    updatedAt: isoDate(index + 4),
  })),
  users,
  tickets,
  promoCodes,
};
